import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Shield, Umbrella, Clock, CheckCircle, XCircle, LogOut } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/member/dashboard`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data?.tier || data.tier === 'none') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-3xl font-heading font-bold mb-4">No Active Membership</h1>
          <p className="text-muted-foreground mb-6">
            Subscribe to a tier to activate your safety net protection.
          </p>
          <Button 
            data-testid="subscribe-now-btn"
            onClick={() => navigate('/select-tier')}
            className="btn-primary"
          >
            Subscribe Now
          </Button>
        </div>
      </div>
    );
  }

  const tierColors = {
    essential: "bg-blue-100 text-blue-800 border-blue-200",
    standard: "bg-purple-100 text-purple-800 border-purple-200",
    plus: "bg-emerald-100 text-emerald-800 border-emerald-200"
  };

  const statusIcons = {
    pending: <Clock className="h-5 w-5 text-yellow-600" />,
    approved: <CheckCircle className="h-5 w-5 text-green-600" />,
    declined: <XCircle className="h-5 w-5 text-red-600" />
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/10 to-background">
      {/* Navigation */}
      <nav className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Umbrella className="h-8 w-8 text-primary" />
            <span className="text-2xl font-heading font-bold text-primary">DueDay</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">Hi, {data?.user?.name}</span>
            <Button 
              data-testid="nav-billing-btn"
              variant="ghost" 
              onClick={() => navigate('/billing')}
              className="rounded-full"
            >
              Billing
            </Button>
            {data?.user?.role === 'admin' && (
              <Button 
                data-testid="nav-admin-btn"
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="rounded-full"
              >
                Admin
              </Button>
            )}
            <Button 
              data-testid="logout-btn"
              variant="ghost" 
              onClick={handleLogout}
              className="rounded-full"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Membership Card */}
          <div className="col-span-1 md:col-span-2 card-feature" data-testid="membership-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Your Membership</p>
                <h2 className="text-3xl font-heading font-bold capitalize">{data.tier} Plan</h2>
              </div>
              <div className={`px-4 py-2 rounded-full border ${tierColors[data.tier] || 'bg-gray-100'}`}>
                <Shield className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Per Event Limit:</span>
                <span className="font-semibold">${data.limits?.per_event || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">30-Day Limit:</span>
                <span className="font-semibold">${data.limits?.per_30_days || 0}</span>
              </div>
            </div>
          </div>

          {/* Quick Action Card */}
          <div className="col-span-1 md:col-span-2 card-feature bg-primary text-primary-foreground" data-testid="quick-action-card">
            <h3 className="text-2xl font-heading font-bold mb-4">Need Help with a Bill?</h3>
            <p className="text-muted-foreground mb-6 opacity-90">
              Submit a request and we'll review it within 24 hours. We're here to help you keep essential bills paid on time.
            </p>
            <Button 
              data-testid="request-help-btn"
              onClick={() => navigate('/request-help')}
              className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium shadow-lg"
            >
              Request Help Now
            </Button>
          </div>

          {/* Recent Requests */}
          <div className="col-span-1 md:col-span-4 card-feature" data-testid="recent-requests-card">
            <h3 className="text-2xl font-heading font-bold mb-6">Recent Requests</h3>
            {data.recent_requests && data.recent_requests.length > 0 ? (
              <div className="space-y-4">
                {data.recent_requests.map((request) => (
                  <div 
                    key={request.request_id}
                    data-testid={`request-${request.request_id}`}
                    className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border"
                  >
                    <div className="flex items-center gap-4">
                      {statusIcons[request.status]}
                      <div>
                        <p className="font-semibold capitalize">{request.bill_type.replace('-', ' ')}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${request.amount_requested}</p>
                      <p className="text-sm text-muted-foreground capitalize">{request.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No requests yet. Submit your first help request above.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
